import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, CardHeader, Collapse, Grid, IconButton, IconButtonProps, Link, styled, Typography, useTheme } from '@mui/material';
import { ExpandMore, LocationOn } from '@mui/icons-material';
import { PersonalDispatchDto } from '../../dtos';
import { Stack } from '@mui/system';
import { LineItemTypes, formatCurrency, formatEasternTime, toEasternDateKey } from '../../util';
import {
    usePatchDispatchArriveMutation,
    usePatchDispatchDepartMutation,
    usePatchDispatchStartMutation,
    usePatchDispatchStopMutation,
    usePatchDispatchPinDispatchMutation,
    usePatchDispatchUnpinDispatchMutation
} from '../../store/generated/generatedApi';
import { useFailedActionSnackbar } from '../../Components/CoreLib/library';
import { BilledToType } from '../../dtos/enums/BilledToType';

interface ExpandMoreProps extends IconButtonProps {
    expand: boolean;
}

interface ExpandableTextProps {
    text: string;
    maxLength?: number;
}

const ExpandableText: FC<ExpandableTextProps> = ({ text, maxLength = 200 }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (text.length <= maxLength) {
        return <Typography>Directions: {text}</Typography>;
    }

    return (
        <Typography>
            Directions: {isExpanded ? text : `${text.substring(0, maxLength)}...`}
            <Button
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(!isExpanded);
                }}
                sx={{ ml: 1, minWidth: 'auto', padding: '0 8px', textTransform: 'none' }}
            >
                {isExpanded ? 'Show Less' : 'Show More'}
            </Button>
        </Typography>
    );
};

const ExpandMoreButton = styled((props: ExpandMoreProps) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
})(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    }),
}));

export interface IDispatchCardProps extends PropsWithChildren {
    dispatch: PersonalDispatchDto;
    isOpenInitially?: boolean;
    isReadonly?: boolean;
    hideExpandMoreButton?: boolean;
    isPinned?: boolean;
    isCompleted?: boolean;
}

export const DispatchCard: FC<IDispatchCardProps> = (props) => {
    const { dispatch, isOpenInitially = false, isReadonly, hideExpandMoreButton, isPinned, isCompleted } = props;
    const [startDispatch, { isLoading: isStartingDispatch, isError: isErrorStartingDispatch, reset: resetStartDispatch }] = usePatchDispatchStartMutation();
    useFailedActionSnackbar('start', 'dispatch', isErrorStartingDispatch, resetStartDispatch);
    const [stopDispatch, { isLoading: isStoppingDispatch, isError: isErrorStoppingDispatch, reset: resetStopDispatch }] = usePatchDispatchStopMutation();
    useFailedActionSnackbar('stop', 'dispatch', isErrorStoppingDispatch, resetStopDispatch);
    const [markDispatchArrival, { isLoading: isMarkingDispatchArrival, isError: isErrorMarkingDispatchArrival, reset: resetMarkDispatchArrival }] =
        usePatchDispatchArriveMutation();
    useFailedActionSnackbar('mark', 'dispatch arrival', isErrorMarkingDispatchArrival, resetMarkDispatchArrival);
    const [markDispatchDeparture, { isLoading: isMarkingDispatchDeparture, isError: isErrorMarkingDispatchDeparture, reset: resetMarkDispatchDeparture }] =
        usePatchDispatchDepartMutation();
    const [pinDispatch] = usePatchDispatchPinDispatchMutation();
    const [unpinDispatch] = usePatchDispatchUnpinDispatchMutation();
    useFailedActionSnackbar('mark', 'dispatch departure', isErrorMarkingDispatchDeparture, resetMarkDispatchDeparture);
    const [expanded, setExpanded] = useState(isOpenInitially);
    const theme = useTheme();

    const isADispatchFromPreviousDay = useMemo(() => {
        return toEasternDateKey(new Date()) > toEasternDateKey(dispatch.arrivalTime);
    }, [dispatch.arrivalTime]);

    const startButtonText = useMemo(() => {
        if (dispatch.lineItemType === LineItemTypes.Hourly) {
            return 'Begin Hourly';
        } else {
            return 'Start';
        }
    }, [dispatch]);

    const endButtonText = useMemo(() => {
        if (dispatch.lineItemType === LineItemTypes.Hourly) {
            return 'End Hourly';
        } else {
            return 'Done Hauling';
        }
    }, [dispatch]);

    const handleExpandClick = useCallback(() => {
        if (hideExpandMoreButton) {
            return;
        }
        setExpanded(!expanded);
    }, [expanded, hideExpandMoreButton]);

    const onPinButtonClicked = useCallback(
        (e: any) => {
            e.stopPropagation();
            pinDispatch({ dispatchId: dispatch.id });
        },
        [pinDispatch, dispatch]
    );

    const onUnpinButtonClicked = useCallback(
        (e: any) => {
            e.stopPropagation();
            unpinDispatch({ dispatchId: dispatch.id });
        },
        [unpinDispatch, dispatch]
    );

    const onStartButtonClicked = useCallback(
        (e: any) => {
            e.stopPropagation();
            startDispatch({ dispatchId: dispatch.id });
        },
        [startDispatch, dispatch]
    );

    const onStopButtonClicked = useCallback(
        (e: any) => {
            e.stopPropagation();
            stopDispatch({ dispatchId: dispatch.id });
        },
        [stopDispatch, dispatch]
    );

    const onArriveButtonClicked = useCallback(
        (e: any) => {
            e.stopPropagation();
            markDispatchArrival({ dispatchId: dispatch.id });
        },
        [markDispatchArrival, dispatch]
    );

    const onDepartButtonClicked = useCallback(
        (e: any) => {
            e.stopPropagation();
            markDispatchDeparture({ dispatchId: dispatch.id });
        },
        [markDispatchDeparture, dispatch]
    );

    const isActiveDispatch = useMemo(() => {
        return dispatch.isActive;
    }, [dispatch.isActive]);

    const departureStatusControls = useMemo(() => {
        switch (dispatch.departureStatus) {
            case 'Arrived':
                return (
                    <Grid item m={2}>
                        <Button
                            variant='contained'
                            color='primary'
                            sx={{ width: '100%' }}
                            disabled={isReadonly || isMarkingDispatchDeparture}
                            onClick={onDepartButtonClicked}>
                            Depart
                        </Button>
                    </Grid>
                );
            case 'Departed':
                return (
                    <Grid item m={2}>
                        <Button
                            variant='contained'
                            color='primary'
                            sx={{ width: '100%' }}
                            disabled={isReadonly || isMarkingDispatchArrival}
                            onClick={onArriveButtonClicked}>
                            Arrive
                        </Button>
                    </Grid>
                );
            default:
                return null;
        }
    }, [isReadonly, isMarkingDispatchDeparture, onDepartButtonClicked, isMarkingDispatchArrival, onArriveButtonClicked, dispatch.departureStatus]);

    return (
        <Card variant='outlined' sx={{ height: expanded ? '100%' : 'auto' }}>
            <CardHeader
                sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                }}
                onClick={handleExpandClick}
                title={
                    <Grid container direction='row' alignItems='center'>
                        <Grid item container direction='column' xs={10}>
                            <Grid item zeroMinWidth>
                                {isPinned ? (
                                    <Typography variant='h6' noWrap>
                                        {dispatch.description} - PINNED
                                    </Typography>
                                ) : (
                                    <Typography variant='h6' noWrap>
                                        {dispatch.description}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item>
                                <Typography>{dispatch.customerNumber ? `${dispatch.customerNumber} - ${dispatch.customerName}` : dispatch.customerName}</Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                }
                action={
                    <ExpandMoreButton
                        expand={expanded}
                        size='small'
                        disableRipple
                        sx={{
                            color: theme.palette.primary.main,
                            backgroundColor: 'white !important',
                            marginTop: '15px',
                            visibility: hideExpandMoreButton ? 'hidden' : undefined,
                        }}>
                        <ExpandMore />
                    </ExpandMoreButton>
                }
            />
            <Collapse in={expanded} timeout='auto' unmountOnExit>
                <CardContent
                    sx={{
                        padding: '8px',
                        paddingBottom: '0px !important',
                    }}>
                    <Box>
                        <Grid container direction='column' onClick={handleExpandClick}>
                            <Grid item>
                                <Typography fontWeight='bold'>{dispatch.projectName}</Typography>
                                <Typography>{dispatch.jobName}</Typography>
                                <Typography>
                                    Order #{dispatch.orderNumber} - Line Item #{dispatch.lineItemNumber}
                                </Typography>
                                <Typography>PO Number: {dispatch.poNumber}</Typography>
                                <Typography>Job Number: {dispatch.customerJobNumber}</Typography>
                            </Grid>
                            {(dispatch.rate !== undefined && dispatch.rate !== null) && <Typography>Rate: {formatCurrency(dispatch.rate ?? 0)} {dispatch.flatRateApplied ? '(Flat Rate)' : ''}</Typography>}
                            <Typography>Material Bill To: {
                                dispatch.isBilledTo === BilledToType.TriAx ? "TRI AX INC" :
                                    dispatch.isBilledTo === BilledToType.Customer ? dispatch.customerName :
                                        dispatch.isBilledTo === BilledToType.Custom ? dispatch.billedTo : ''
                            }</Typography>

                            {dispatch.driverComment && <Typography sx={{ whiteSpace: 'pre-wrap' }}><strong>Driver Comment:</strong> {dispatch.driverComment}</Typography>}
                            {dispatch.driverMemo && <Typography sx={{ whiteSpace: 'pre-wrap' }}><strong>Order Memo:</strong> {dispatch.driverMemo}</Typography>}

                            <Grid item mt={1}>
                                <Typography fontWeight='bold'>Times</Typography>
                                <Typography>Yard Time: {dispatch.yardTime !== null ? formatEasternTime(dispatch.yardTime) : 'ASAP'}</Typography>
                                <Typography>Onsite Time: {formatEasternTime(dispatch.arrivalTime)}</Typography>
                            </Grid>
                            {dispatch.haulFromAddress && (
                                <>
                                    <Grid item mt={2}>
                                        <Typography fontWeight='bold'>Haul From</Typography>
                                    </Grid>
                                    <Grid item>
                                        <Stack direction='row'>
                                            <LocationOn color='primary' />
                                            <Typography>
                                                <Link
                                                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dispatch.haulFromAddress)}&travelmode=driving`}
                                                    target='_blank'
                                                    onClick={(e) => e.stopPropagation()}>
                                                    {dispatch.haulFromAddress}
                                                </Link>
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                    {dispatch.haulFromDirections && (
                                        <Grid item>
                                            <ExpandableText text={dispatch.haulFromDirections} maxLength={350} />
                                        </Grid>
                                    )}
                                    {dispatch.haulFromSiteName && (
                                        <Grid item>
                                            <Typography>Site Name: {dispatch.haulFromSiteName}</Typography>
                                        </Grid>
                                    )}
                                    {dispatch.haulFromRegion && (
                                        <Grid item>
                                            <Typography>Region: {dispatch.haulFromRegion}</Typography>
                                        </Grid>
                                    )}
                                </>
                            )}
                            {dispatch.haulToAddress && (
                                <>
                                    <Grid item mt={2}>
                                        <Typography fontWeight='bold'>Haul To</Typography>
                                    </Grid>
                                    <Grid item>
                                        <Stack direction='row'>
                                            <LocationOn color='primary' />
                                            <Typography>
                                                <Link
                                                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dispatch.haulToAddress)}&travelmode=driving`}
                                                    target='_blank'
                                                    onClick={(e) => e.stopPropagation()}>
                                                    {dispatch.haulToAddress}
                                                </Link>
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                    {dispatch.haulToDirections && (
                                        <Grid item>
                                            <ExpandableText text={dispatch.haulToDirections} maxLength={350} />
                                        </Grid>
                                    )}
                                    {
                                        dispatch.haulToSiteName && (
                                            <Grid item>
                                                <Typography>Site Name: {dispatch.haulToSiteName}</Typography>
                                            </Grid>
                                        )
                                    }
                                    {dispatch.haulToRegion && (
                                        <Grid item>
                                            <Typography>Region: {dispatch.haulToRegion}</Typography>
                                        </Grid>
                                    )}
                                </>
                            )}
                            <Grid item mt={2}>
                                <Typography fontWeight='bold'>Task</Typography>
                            </Grid>
                            <Grid item>
                                <Typography>{dispatch.description}</Typography>
                            </Grid>
                            <Grid item>
                                <Typography>{dispatch.task}</Typography>
                            </Grid>
                            <Grid item mt={1}>
                                <Typography fontWeight='bold'>({dispatch.equipmentInfo.length} Additional Drivers on Job)</Typography>
                            </Grid>
                            <Grid item container direction='column'>
                                {dispatch.equipmentInfo?.map((info, idx) => (
                                    <Grid item key={`info-${idx}`}>
                                        <Typography>{info}</Typography>
                                    </Grid>
                                ))}
                            </Grid>
                            <Grid item mt={1}>
                                <Typography fontWeight='bold'>Ordered By</Typography>
                            </Grid>
                            {dispatch.orderOrderedByContact !== null && (
                                <Grid item container direction='column'>
                                    <Grid item>
                                        <Typography>
                                            {dispatch.orderOrderedByContact?.contactName}
                                        </Typography>

                                        {dispatch.orderOrderedByContact?.cellPhone !== null && dispatch.orderOrderedByContact?.cellPhone !== ''
                                            ? (
                                                <Typography>
                                                    {'Cell #: '}
                                                    <Link href={`tel:${dispatch.orderOrderedByContact?.cellPhone}`} onClick={(e) => e.stopPropagation()}>
                                                        {dispatch.orderOrderedByContact?.cellPhone}
                                                    </Link>
                                                </Typography>
                                            )
                                            : ''}
                                        {dispatch.orderOrderedByContact?.landlinePhone !== null && dispatch.orderOrderedByContact?.landlinePhone !== ''
                                            ? (
                                                <Typography>
                                                    {'Landline #: '}
                                                    <Link href={`tel:${dispatch.orderOrderedByContact?.landlinePhone}`} onClick={(e) => e.stopPropagation()}>
                                                        {dispatch.orderOrderedByContact?.landlinePhone}
                                                    </Link>
                                                </Typography>
                                            )
                                            : ''}
                                        {dispatch.orderOrderedByContact?.email !== null && dispatch.orderOrderedByContact?.email !== ''
                                            ? (
                                                <Typography>
                                                    {'Email: '}
                                                    <Link href={`mailto:${dispatch.orderOrderedByContact?.email}`} onClick={(e) => e.stopPropagation()}>
                                                        {dispatch.orderOrderedByContact?.email}
                                                    </Link>
                                                </Typography>
                                            )
                                            : ''}
                                    </Grid>
                                </Grid>
                            )}
                            <Grid item mt={1}>
                                <Typography fontWeight='bold'>Order Contact</Typography>
                            </Grid>
                            {dispatch.orderContact !== null && (
                                <Grid item container direction='column'>
                                    <Grid item>
                                        <Typography>
                                            {dispatch.orderContact?.contactName}
                                        </Typography>
                                        {dispatch.orderContact?.cellPhone !== null && dispatch.orderContact?.cellPhone !== ''
                                            ? (
                                                <Typography>
                                                    {'Cell #: '}
                                                    <Link href={`tel:${dispatch.orderContact?.cellPhone}`} onClick={(e) => e.stopPropagation()}>
                                                        {dispatch.orderContact?.cellPhone}
                                                    </Link>
                                                </Typography>
                                            )
                                            : ''}
                                        {dispatch.orderContact?.landlinePhone !== null && dispatch.orderContact?.landlinePhone !== ''
                                            ? (
                                                <Typography>
                                                    {'Landline #: '}
                                                    <Link href={`tel:${dispatch.orderContact?.landlinePhone}`} onClick={(e) => e.stopPropagation()}>
                                                        {dispatch.orderContact?.landlinePhone}
                                                    </Link>
                                                </Typography>
                                            )
                                            : ''}
                                        {dispatch.orderContact?.email !== null && dispatch.orderContact?.email !== ''
                                            ? (
                                                <Typography>
                                                    {'Email: '}
                                                    <Link href={`mailto:${dispatch.orderContact?.email}`} onClick={(e) => e.stopPropagation()}>
                                                        {dispatch.orderContact?.email}
                                                    </Link>
                                                </Typography>
                                            )
                                            : ''}
                                    </Grid>
                                </Grid>
                            )}
                            <Grid item mt={1}>
                                <Typography fontWeight='bold'>Your Equipment</Typography>
                                <Typography>
                                    {dispatch.yourEquipment?.equipmentNumber} - {dispatch.yourEquipment?.equipmentType}
                                </Typography>
                                {dispatch.yourEquipment?.year && <Typography>Year: {dispatch.yourEquipment?.year}</Typography>}
                                {dispatch.yourEquipment?.make && <Typography>Make: {dispatch.yourEquipment?.make}</Typography>}
                                {dispatch.yourEquipment?.model && <Typography>Model: {dispatch.yourEquipment?.model}</Typography>}
                                {dispatch.yourEquipment?.vin && <Typography>VIN: {dispatch.yourEquipment?.vin}</Typography>}
                            </Grid>
                            {isCompleted ? (
                                <>
                                    <Grid item m={2}>
                                        <Button
                                            variant='contained'
                                            color='primary'
                                            sx={{ width: '100%' }}
                                            onClick={onStartButtonClicked}
                                            disabled={isStartingDispatch || isADispatchFromPreviousDay}>
                                            Restart Dispatch
                                        </Button>
                                    </Grid>
                                </>
                            ) : <></>}
                            {!isReadonly ? (
                                isActiveDispatch ? (
                                    <>
                                        {!isPinned ? (
                                            <Grid item m={2}>
                                                <Button
                                                    variant='contained'
                                                    color='primary'
                                                    sx={{ width: '100%' }}
                                                    disabled={isReadonly || isStoppingDispatch}
                                                    onClick={onPinButtonClicked}>
                                                    Pin Dispatch
                                                </Button>
                                            </Grid>
                                        ) : (
                                            <Grid item m={2}>
                                                <Button
                                                    variant='contained'
                                                    color='primary'
                                                    sx={{ width: '100%' }}
                                                    disabled={isReadonly || isStoppingDispatch}
                                                    onClick={onUnpinButtonClicked}>
                                                    Unpin Dispatch
                                                </Button>
                                            </Grid>
                                        )}
                                        {departureStatusControls}
                                        <Grid item m={2}>
                                            <Button
                                                variant='contained'
                                                color='primary'
                                                sx={{ width: '100%' }}
                                                disabled={isReadonly || isStoppingDispatch}
                                                onClick={onStopButtonClicked}>
                                                {endButtonText}
                                            </Button>
                                        </Grid>
                                    </>
                                ) : (
                                    <Grid item m={2}>
                                        <Button
                                            variant='contained'
                                            color='primary'
                                            sx={{ width: '100%' }}
                                            disabled={isReadonly || isStartingDispatch}
                                            onClick={onStartButtonClicked}>
                                            {startButtonText}
                                        </Button>
                                    </Grid>
                                )
                            ) : (
                                <Grid item m={2}></Grid>
                            )}
                        </Grid>
                    </Box>
                </CardContent>
            </Collapse>
        </Card>
    );
};
